// Support (contracts/messaging.md §4): players write from Profile → Help & support; the team answers here. The
// thread list puts conversations waiting on the team first; opening one marks it read (the GET does it) and
// polls it every 15 s. Editors reply (a push reaches the player at once, in their language), close and
// reopen; every role reads.

import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useDeferredValue, useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router";
import { LOCALE_LABEL, type Locale } from "../api/messaging";
import { REPLY_MAX, SUPPORT_FILTERS, type SupportFilter, supportKeys, useSupportReply, useSupportStatus, useSupportThread, useSupportThreads } from "../api/support";
import { useAuth, useCan } from "../auth/AuthContext";
import { Button, Chip, Dialog, EmptyState, ErrorState, InlineError, Loading, PageHeader, Segmented, TextInput } from "../components/ui";
import { formatDateTime, formatLongDate } from "../lib/dates";
import { fmtInt, shortId } from "../lib/format";
import { timeAgo } from "../lib/messaging";
import { type Picked, PlayerPicker } from "./messaging/components";
import styles from "./pages.module.css";
import s from "./support.module.css";

const FILTER_LABEL: Record<SupportFilter, string> = { open: "Open", closed: "Closed", all: "All" };
const localeName = (l: string | null) => (l ? (LOCALE_LABEL[l as Locale] ?? l) : "language unknown");
const who = (handle: string | null, userId: string) => (handle ? `@${handle}` : shortId(userId));

export function SupportPage() {
  const { userId } = useParams();
  const canReply = useCan("support.reply");
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<SupportFilter>("open");
  const [q, setQ] = useState("");
  const search = useDeferredValue(q.trim());
  const list = useSupportThreads(filter, search);
  const [starting, setStarting] = useState(false);

  return (
    <>
      <PageHeader
        title="Support"
        meta={
          canReply ? (
            <span>Players write from Profile → Help & support and get a push when you reply. Replies ignore quiet hours and the daily cap.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · replying needs the editor role (you are {admin?.role})
            </span>
          )
        }
        actions={
          canReply ? (
            <Button variant="secondary" onClick={() => setStarting(true)}>
              Write to a player…
            </Button>
          ) : null
        }
      />
      <div className={s.panes}>
        <section className={s.listPane} aria-label="Conversations">
          <div className={s.listHead}>
            <Segmented label="Status" value={filter} onChange={setFilter} options={SUPPORT_FILTERS.map((f) => ({ value: f, label: FILTER_LABEL[f] }))} />
            <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="@handle, invite code or user id" data-search data-testid="support-search" aria-label="Search conversations" />
          </div>
          {list.isPending ? (
            <div className={s.padded}>
              <Loading rows={6} />
            </div>
          ) : list.isError ? (
            <div className={s.padded}>
              <ErrorState error={list.error} onRetry={() => void list.refetch()} />
            </div>
          ) : list.data.items.length === 0 ? (
            <EmptyState title={search ? "No conversation found" : filter === "open" ? "Nothing open" : "No conversations"}>
              {search ? "Try a handle prefix, an invite code or a full user id." : "Players' messages show up here."}
            </EmptyState>
          ) : (
            <ul className={s.threads} data-testid="threads">
              {list.data.items.map((t) => (
                <li key={t.userId}>
                  <NavLink to={`/support/${t.userId}`} className={s.thread} data-unread={t.teamUnread > 0 || undefined} data-testid="thread">
                    <div className={s.threadTop}>
                      <span className={s.threadName}>{who(t.handle, t.userId)}</span>
                      {t.pro ? <Chip tone="accent">Pro</Chip> : null}
                      {t.status === "closed" ? <Chip tone="muted">closed</Chip> : null}
                      <span className={s.threadTime}>{timeAgo(t.lastMessage.createdAt)}</span>
                    </div>
                    <div className={s.threadBottom}>
                      <span className={s.threadPreview}>
                        {t.lastMessage.sender === "team" ? <span className={s.you}>Team: </span> : null}
                        {t.lastMessage.body}
                      </span>
                      {t.teamUnread > 0 ? (
                        <span className={s.unread} aria-label={`${t.teamUnread} unread`} data-testid="thread-unread">
                          {t.teamUnread}
                        </span>
                      ) : null}
                    </div>
                    <span className={s.threadLocale}>{localeName(t.locale)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className={s.threadPane} aria-label="Conversation">
          {userId ? (
            <Conversation key={userId} userId={userId} canReply={canReply} />
          ) : (
            <EmptyState title="Pick a conversation">Opening one marks it read for the team.</EmptyState>
          )}
        </section>
      </div>
      <StartDialog
        open={starting}
        onClose={() => setStarting(false)}
        onPick={(p) => {
          setStarting(false);
          navigate(`/support/${p.userId}`);
        }}
      />
    </>
  );
}

function Conversation({ userId, canReply }: { userId: string; canReply: boolean }) {
  const thread = useSupportThread(userId);
  const client = useQueryClient();
  const reply = useSupportReply();
  const status = useSupportStatus();
  const [text, setText] = useState("");
  const scroller = useRef<HTMLOListElement>(null);
  const count = thread.data?.messages.length ?? 0;

  // Each fetch marks the conversation read: refresh the list and the nav badge.
  useEffect(() => {
    if (!thread.dataUpdatedAt) return;
    void client.invalidateQueries({ queryKey: supportKeys.lists });
    void client.invalidateQueries({ queryKey: supportKeys.summary });
  }, [thread.dataUpdatedAt, client]);

  // The newest message in view.
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count]);

  if (thread.isPending || thread.isError) {
    return (
      <div className={s.padded}>{thread.isPending ? <Loading rows={8} /> : <ErrorState error={thread.error} onRetry={() => void thread.refetch()} />}</div>
    );
  }
  const { player, messages } = thread.data;
  const closed = thread.data.thread.status === "closed";
  const body = text.trim();

  const send = (e?: FormEvent) => {
    e?.preventDefault();
    if (!body || body.length > REPLY_MAX || reply.isPending) return;
    reply.mutate({ userId, body }, { onSuccess: () => setText("") });
  };

  return (
    <div className={s.conversation}>
      <header className={s.player} data-testid="player-card">
        <div className={s.playerMain}>
          <Link to={`/players/${player.userId}`} className={s.playerName}>
            {who(player.handle, player.userId)}
          </Link>
          <div className={s.playerChips}>
            <Chip tone={player.pro ? "accent" : "muted"}>{player.pro ? "Pro" : "Free"}</Chip>
            <Chip tone="neutral">{localeName(player.locale)}</Chip>
            <Chip tone={player.signedIn ? "info" : "muted"}>{player.signedIn ? "Signed in" : "Guest"}</Chip>
          </div>
          <span className={s.playerMeta}>
            Streak {fmtInt(player.streak)} · joined {formatLongDate(player.createdAt.slice(0, 10))} · <span className="mono">{shortId(player.userId)}</span>
          </span>
        </div>
        <div className={s.playerSide}>
          <Chip tone={closed ? "muted" : "success"}>{thread.data.thread.status}</Chip>
          {canReply && messages.length > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              busy={status.isPending}
              onClick={() => status.mutate({ userId, status: closed ? "open" : "closed" })}
              data-testid="thread-status"
            >
              {closed ? "Reopen" : "Close"}
            </Button>
          ) : null}
        </div>
      </header>
      <InlineError error={status.error} />

      {messages.length === 0 ? (
        <div className={s.emptyThread}>
          <EmptyState title="No messages yet">{canReply ? "Write first: the player gets a push and finds it in Help & support." : "This player hasn't written to support."}</EmptyState>
        </div>
      ) : (
        <ol className={s.messages} ref={scroller} aria-label="Messages" data-testid="messages">
          {messages.map((msg) => (
            <li key={msg.id} className={s.message} data-sender={msg.sender} data-testid="message">
              <div className={s.bubble}>{msg.body}</div>
              <span className={s.messageMeta}>
                {msg.sender === "team" ? `${msg.adminEmail ?? "IQ Daily team"} · ` : ""}
                {formatDateTime(msg.createdAt)}
              </span>
            </li>
          ))}
        </ol>
      )}

      {canReply ? (
        <form className={s.reply} onSubmit={send}>
          <textarea
            className={styles.textarea}
            value={text}
            maxLength={REPLY_MAX}
            rows={3}
            placeholder={`Reply to ${who(player.handle, player.userId)}…`}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
            }}
            data-testid="reply"
            aria-label="Reply"
          />
          <div className={s.replyFoot}>
            <span className={styles.faint}>
              {text.length}/{REPLY_MAX} · ⌘↵ sends{closed ? " · replying reopens the conversation" : ""}
            </span>
            <Button type="submit" variant="primary" size="sm" busy={reply.isPending} disabled={!body} data-testid="reply-send">
              Send reply
            </Button>
          </div>
          <InlineError error={reply.error} />
        </form>
      ) : null}
    </div>
  );
}

function StartDialog({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (p: Picked) => void }) {
  const [picked, setPicked] = useState<Picked | null>(null);
  return (
    <Dialog
      open={open}
      title="Write to a player"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!picked} onClick={() => picked && onPick(picked)}>
            Open conversation
          </Button>
        </>
      }
    >
      <p className={styles.prose}>Opens the player's support conversation, empty if they never wrote. Your first message reaches them as a push.</p>
      <PlayerPicker picked={picked} onPick={setPicked} />
    </Dialog>
  );
}
